import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import adminApi from '../services/adminApi';
import { formatApiError } from '../utils/errorMessage';
import SubscriptionModal from '../components/SubscriptionModal';
import SubscriptionStatusBadge from '../components/SubscriptionStatusBadge';
import { formatCurrency, getSubscriptionSummary } from '../utils/subscriptionHelpers';
import ErrorBoundary from '../components/ErrorBoundary';
import { parseCalendarDay } from '../utils/localDate';

const AdminUsers = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [memberships, setMemberships] = useState({}); // by userId: [{business_id,...}]
  const [memberForUserId, setMemberForUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '', role: 'user' });
  const [subscriptionModalBusiness, setSubscriptionModalBusiness] = useState(null);
  const [resettingPasswordFor, setResettingPasswordFor] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  const canView = user && (user.is_superuser || user.role === 'admin');

  useEffect(() => {
    if (canView) {
      loadUsers();
      loadBusinesses();
    }
  }, [canView]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await adminApi.listUsers();
      setUsers(res.data);
      setError('');
    } catch (e) {
      setError(formatApiError(e, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  };

  const loadBusinesses = async () => {
    try {
      const res = await adminApi.listBusinesses();
      setBusinesses(res.data || []);
    } catch (e) {
      // non-fatal
      console.error('Failed to load businesses:', e?.response?.status || e?.code || 'no response', e?.response?.data?.code || '');
    }
  };

  const handleSubscriptionSuccess = () => {
    loadBusinesses();
  };

  const openSubscriptionModal = (business) => {
    setSubscriptionModalBusiness(business);
  };

  const loadMemberships = async (userId) => {
    try {
      const res = await adminApi.getUserMemberships(userId);
      setMemberships((prev) => ({ ...prev, [userId]: res.data || [] }));
    } catch (e) {
      setMemberships((prev) => ({ ...prev, [userId]: [] }));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setCreating(true);
      await adminApi.createUser(form);
      setForm({ email: '', password: '', first_name: '', last_name: '', role: 'user' });
      loadUsers();
    } catch (e) {
      setError(formatApiError(e, 'Failed to create user'));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await adminApi.updateUser(u.id, { is_active: !u.is_active });
      loadUsers();
    } catch (e) {
      setError(formatApiError(e, 'Failed to update user'));
    }
  };

  const setRole = async (u, role) => {
    try {
      await adminApi.updateUser(u.id, { role });
      loadUsers();
    } catch (e) {
      setError(formatApiError(e, 'Failed to update role'));
    }
  };

  const openMembershipManager = async (u) => {
    setMemberForUserId(u.id);
    await loadMemberships(u.id);
  };

  const isMember = (userId, businessId) => {
    const list = memberships[userId] || [];
    return !!list.find(m => m.business_id === businessId);
  };

  const toggleMembership = async (userId, businessId) => {
    try {
      const currently = isMember(userId, businessId);
      if (currently) {
        await adminApi.removeMember(businessId, userId);
      } else {
        await adminApi.addMember(businessId, { user_id: userId, member_role: 'member' });
      }
      await loadMemberships(userId);
    } catch (e) {
      setError(formatApiError(e, 'Failed to update membership'));
    }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    try {
      setResetting(true);
      await adminApi.resetUserPassword(userId, newPassword);
      setResettingPasswordFor(null);
      setNewPassword('');
      setError('');
      alert('Password reset successfully');
    } catch (e) {
      setError(formatApiError(e, 'Failed to reset password'));
    } finally {
      setResetting(false);
    }
  };

  if (!canView) {
    return <div className="p-6">You do not have permission to view this page.</div>;
  }

  return (
    <ErrorBoundary section="Admin Users">
      <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
          <p className="text-gray-600">{user.is_superuser ? 'Superuser: manage all users' : 'Admin: manage users you created'}</p>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded" role="alert">
          {formatApiError(error, 'An error occurred')}
        </div>
      )}

      <div className="bg-white rounded shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Create User</h3>
        <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input type="email" required placeholder="Email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} className="border rounded px-3 py-2" />
          <input type="password" required placeholder="Password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} className="border rounded px-3 py-2" />
          <input type="text" placeholder="First name" value={form.first_name} onChange={e=>setForm({...form,first_name:e.target.value})} className="border rounded px-3 py-2" />
          <input type="text" placeholder="Last name" value={form.last_name} onChange={e=>setForm({...form,last_name:e.target.value})} className="border rounded px-3 py-2" />
          {user.is_superuser && (
            <select value={form.role} onChange={e=>setForm({...form,role:e.target.value})} className="border rounded px-3 py-2">
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="superuser">Superuser</option>
            </select>
          )}
          <div className="md:col-span-4 flex justify-end">
            <button type="submit" disabled={creating} className="px-4 py-2 bg-blue-600 text-white rounded disabled:bg-blue-300">{creating ? 'Creating...' : 'Create'}</button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded shadow">
        <div className="p-6 border-b"><h3 className="text-lg font-semibold">Users</h3></div>
        <div className="divide-y">
          {loading ? (
            <div className="p-6">Loading...</div>
          ) : users.length === 0 ? (
            <div className="p-6 text-gray-500">No users found</div>
          ) : (
            users.map((u) => (
              <div key={u.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                  <div className="font-medium">{u.email}</div>
                  <div className="text-sm text-gray-500">{u.first_name || ''} {u.last_name || ''}</div>
                  </div>
                  <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 rounded text-xs ${u.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{u.is_active ? 'Active' : 'Inactive'}</span>
                  <span className="text-xs text-gray-600">Role: {u.role || (u.is_superuser ? 'superuser' : 'user')}</span>
                  <button onClick={() => toggleActive(u)} className="px-3 py-1 border rounded">{u.is_active ? 'Disable' : 'Enable'}</button>
                  {user.is_superuser && (
                    <>
                      <select value={u.role || (u.is_superuser ? 'superuser' : 'user')} onChange={(e)=>setRole(u, e.target.value)} className="border rounded px-2 py-1 text-sm">
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="superuser">Superuser</option>
                      </select>
                      <button 
                        onClick={() => {
                          setResettingPasswordFor(u.id);
                          setNewPassword('');
                        }} 
                        className="px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm"
                      >
                        Reset Password
                      </button>
                    </>
                  )}
                    <button onClick={() => openMembershipManager(u)} className="px-3 py-1 border rounded">Manage Access</button>
                  </div>
                </div>

                {resettingPasswordFor === u.id && (
                  <div className="mt-4 border-t pt-4">
                    <div className="text-sm font-semibold mb-2">Reset Password for {u.email}</div>
                    <div className="flex items-center gap-2">
                      <input
                        type="password"
                        placeholder="Enter new password (min 8 characters)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="border rounded px-3 py-2 flex-1"
                        minLength={8}
                      />
                      <button
                        onClick={() => handleResetPassword(u.id)}
                        disabled={resetting || !newPassword || newPassword.length < 8}
                        className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        {resetting ? 'Resetting...' : 'Confirm Reset'}
                      </button>
                      <button
                        onClick={() => {
                          setResettingPasswordFor(null);
                          setNewPassword('');
                        }}
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {memberForUserId === u.id && (
                  <div className="mt-4 border-t pt-4">
                    <div className="text-sm font-semibold mb-2">Business Access</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {businesses.map(b => (
                        <label key={b.id} className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={isMember(u.id, b.id)} onChange={() => toggleMembership(u.id, b.id)} />
                          <span>{b.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Businesses with Subscriptions Section - Superuser Only */}
      {user.is_superuser && (
        <div className="bg-white rounded shadow">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold">Businesses & Subscriptions</h3>
            <p className="text-sm text-gray-500 mt-1">Manage subscriptions for each business</p>
          </div>
          <div className="divide-y">
            {loading ? (
              <div className="p-6">Loading...</div>
            ) : businesses.length === 0 ? (
              <div className="p-6 text-gray-500">No businesses found</div>
            ) : (
              businesses.map((b) => (
                <div key={b.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{b.name || 'Unnamed Business'}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        {b.legal_name && <span>{b.legal_name} • </span>}
                        Owner: {b.user_id ? `User #${b.user_id}` : 'N/A'}
                      </div>
                      {b.subscription_id && (
                        <div className="mt-2 text-sm text-gray-600">
                          <div>Plan: {getSubscriptionSummary(b)}</div>
                          {b.current_period_end && (
                            <div>Period ends: {parseCalendarDay(b.current_period_end).toLocaleDateString()}</div>
                          )}
                          {b.total_paid > 0 && (
                            <div>Total paid: {formatCurrency(b.total_paid)}</div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <SubscriptionStatusBadge subscription={b} />
                      <button
                        onClick={() => openSubscriptionModal(b)}
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                      >
                        Manage Subscription
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      <SubscriptionModal
        business={subscriptionModalBusiness}
        isOpen={!!subscriptionModalBusiness}
        onClose={() => setSubscriptionModalBusiness(null)}
        onSuccess={handleSubscriptionSuccess}
      />
      </div>
    </ErrorBoundary>
  );
};

export default AdminUsers;
